import { RangeSlider } from '@gleamer/ui';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@ohif/ui-next';
import classNames from 'classnames';
import React from 'react';
import {
  usePredictionsConfidences,
  usePredictionsTask,
} from '../contexts/predictions.hooks';

/**
 * "AI Confidences" collapsed into a hover card.
 *
 * The inline version spent ~300px of header on a control that is adjusted
 * rarely, which starved the toolbar. Compacting it in place recovered the width
 * but dropped the wording to "AI 20-100", which does not say what the numbers
 * are for. Here the trigger keeps the full label plus the current range, and the
 * slider appears on hover.
 *
 * HoverCard rather than a hand-rolled hover on a Popover: Radix owns the
 * open/close, including the pointer travelling from the trigger into the panel.
 * Driving a Popover's `open` from `onMouseEnter` makes the trigger fight itself
 * — the pointer entering opens it and the click that follows toggles it shut.
 */
export function ConfidencesSlider({ className }: { className?: string }) {
  const task = usePredictionsTask();
  const predictionsConfiguration = task?.predictionsConfiguration;

  const [confidences, setConfidences] = usePredictionsConfidences();

  const [min, max] = confidences ?? [];
  const low = min ?? predictionsConfiguration?.minConfidence;
  const high = max ?? predictionsConfiguration?.maxConfidence;

  return (
    <HoverCard
      openDelay={100}
      closeDelay={200}
    >
      <HoverCardTrigger asChild>
        <button
          type="button"
          data-cy="ai-confidences-trigger"
          className={classNames(
            'flex shrink-0 items-center gap-1 whitespace-nowrap rounded px-2 py-1 text-xs text-white hover:bg-white/10',
            className
          )}
        >
          <span>AI Confidences</span>
          <span className="tabular-nums opacity-80">
            {low}–{high}
          </span>
        </button>
      </HoverCardTrigger>
      <HoverCardContent
        align="end"
        sideOffset={4}
        // Match the trigger's width so the panel reads as an extension of the
        // "AI Confidences 0-100" block rather than a floating dialog, and hold
        // nothing but the slider — the label and range are already on the
        // trigger right above it. The handles are drawn centred on the track
        // ends and overhang it by half their width, so the horizontal padding
        // has to clear them or they sit on the border.
        // `min-w` because the two constraints conflict at this font size: the
        // trigger is ~134px, which leaves ~100px of track for two 24px handles
        // and they end up sitting on the border. The panel tracks the trigger's
        // width but will not go below a width the slider is actually usable at.
        className="w-[var(--radix-hover-card-trigger-width)] min-w-[200px] px-4 py-3"
      >
        <RangeSlider
          setValues={setConfidences}
          values={confidences}
          data-testid="predictions-slider"
          min={predictionsConfiguration?.minConfidence}
          max={predictionsConfiguration?.maxConfidence}
          className="!mx-0 !w-full"
        />
      </HoverCardContent>
    </HoverCard>
  );
}
